import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import '../../App.css';
import { AuthContext } from '../../context/AuthContext';
import AddRegistrationResultDialog from '../dialogs/addRegistrationResultDialog';
import { Badge } from '../ui/badge';
import { DataTable } from './datatable';
import DatatableSortButton from './datatableSortButton';

export const columns = (fetchClass, competitionClass, level, auth, dataLength) => {
    const isAdminOrPrivilegedUser = auth?.user?.superadmin || auth?.user?.bunnyadmin || auth?.user?.judge;

    const baseColumns = [
        {
            id: "bunnyName",
            accessorKey: "bunny.name",
            header: ({ column }) => (
                <DatatableSortButton column={column}>Kanin</DatatableSortButton>
            ),
            cell: info => {
                const bunny = info.row.original.bunny || {};
                return (
                    <Link
                        to={`/bunny/${bunny.id || ''}`}
                        className='text-blue-600 font-medium hover:underline'
                    >
                        {bunny.name || 'Unnamed'}
                    </Link>
                );
            },
        },
        {
            accessorKey: "driver_name",
            accessorFn: (row) => row.driver?.first_name, // extract the nested first name
            header: ({ column }) => <DatatableSortButton column={column}>Förare</DatatableSortButton>,
            cell: info => {
                const driver = info.row.original.driver || {};
                return `${driver.first_name || 'Unknown'} ${driver.last_name || ''}`;
            },
        },
        {
            accessorKey: "errors",
            header: ({ column }) => <DatatableSortButton column={column}>Fel</DatatableSortButton>,
            cell: info => {
                const results = info.row.original.straightCrookedResults || [];
                const attempt = results[level - 1] || {}; // Safely access level-1 index
                if (attempt.errors >= 99) {
                    return 'Ut';
                }
                return attempt.errors != null ? attempt.errors : 'Inga resultat ännu.'; // Handle null or undefined
            },
            sortingFn: (rowA, rowB) => {
                const a = rowA.original.straightCrookedResults?.[level - 1]?.errors ?? Infinity;
                const b = rowB.original.straightCrookedResults?.[level - 1]?.errors ?? Infinity;

                return a - b; // Sort numerically, with null/undefined errors treated as the largest value
            },
        },
        {
            accessorKey: "time",
            header: ({ column }) => <DatatableSortButton column={column}>Tid</DatatableSortButton>,
            cell: info => {
                const results = info.row.original.straightCrookedResults || [];
                const attempt = results[level - 1] || {}; // Safely access level-1 index
                if (attempt.time >= 1000) {
                    return 'Ut';
                }
                return attempt.time != null ? attempt.time : 'Inga resultat ännu.'; // Handle null or undefined
            },
            sortingFn: (rowA, rowB) => {
                const a = parseFloat(rowA.original.straightCrookedResults?.[level - 1]?.time) || Infinity;
                const b = parseFloat(rowB.original.straightCrookedResults?.[level - 1]?.time) || Infinity;

                return a - b; // Sort numerically, with missing time treated as the largest value
            },
        },
    ];

    const columnConfig = competitionClass?.finished_at
        ? {
            accessorKey: "placing",
            header: ({ column }) => <DatatableSortButton column={column}>Plac.</DatatableSortButton>,
            sortingFn: (rowA, rowB, columnId) => {
                const valueA = rowA.getValue(columnId);
                const valueB = rowB.getValue(columnId);

                const getSortableValue = (value) => {
                    if (value === null || value === "Ej plac.") return Number.MAX_SAFE_INTEGER; // Ensure 'text' goes last
                    return parseInt(value, 10) || 0; // Parse valid numbers, default to 0 if NaN
                };

                const placeA = getSortableValue(valueA);
                const placeB = getSortableValue(valueB);

                return placeA - placeB;
            },
            cell: info => {
                const bunny = info.row.original || {}; // Safely access bunny
                const placing = info.getValue() || "Ej plac."; // Display placing or a placeholder

                return (
                    <div className='flex items-center'>
                        <span>{placing}</span>
                        {bunny.crossedOut ? (
                            <Badge className='text-nowrap' variant='destructive'>S</Badge>
                        ) : bunny.outOfCompetition ? (
                            <Badge className='text-nowrap' variant='secondary'>UT</Badge>
                        ) : bunny.only_cup ? (
                            <Badge className='text-nowrap' variant='outline'>EC</Badge>
                        ) : null}
                    </div>
                );
            },
            size: 10,
        }
        : {
            accessorKey: "startNumber",
            header: ({ column }) => <DatatableSortButton column={column}>Snr.</DatatableSortButton>,
            cell: info => info.row.original.startNumber,
            sortingFn: (rowA, rowB, columnId) => {
                const a = rowA.getValue(columnId);
                const b = rowB.getValue(columnId);

                // Handle undefined or null values (place them at the bottom)
                if (a == null) return 1; // `a` is undefined/null, so it should come after `b`
                if (b == null) return -1; // `b` is undefined/null, so it should come after `a`

                // Perform a numeric comparison if both are defined
                return a - b;
            },
            cell: info => {
                const bunny = info.row.original || {}; // Safely access bunny
                const startNumber = info.row.original.startNumber || ''; // Safely get startNumber

                return (
                    <div className='flex items-center'>
                        <span>{startNumber}</span>
                        {bunny.crossedOut ? (
                            <Badge className='ml-3 text-nowrap' variant='destructive'>S</Badge>
                        ) : bunny.outOfCompetition ? (
                            <Badge className='ml-3 text-nowrap' variant='secondary'>UT</Badge>
                        ) : bunny.only_cup ? (
                            <Badge className='ml-3 text-nowrap' variant='outline'>EC</Badge>
                        ) : null}
                    </div>
                );
            },
            size: 10,
        };

    // Insert the determined column at the start of baseColumns
    baseColumns.splice(0, 0, columnConfig);

    if (isAdminOrPrivilegedUser && dataLength > 0 && !competitionClass?.finished_at) {
        baseColumns.push(
            {
                accessorKey: "add_result",
                header: "Lägg till resultat",
                enableHiding: false,
                cell: info => {
                    const registration = info.row.original || {};
                    const results = registration.straightCrookedResults || [];
                    const attempt = results[level - 1] || {}; // Safely access level-1 index
                    const hasResult = attempt.errors != null || attempt.time != null;
                    const competitionFinished = competitionClass?.finished_at;

                    const currentRoundPassed = results.some(result => result.round > level); // Check if any result exists for a later round

                    if (currentRoundPassed || competitionFinished) {
                        return <span className='text-muted-foreground'>Resultat tillagt</span>;
                    }

                    return hasResult ? (
                        <AddRegistrationResultDialog
                            registration={registration}
                            sportClassId={competitionClass.sport_class_id}
                            fetchClass={fetchClass}
                            round={level}
                            action={'Ändra resultat'}
                        />
                    ) : (
                        <AddRegistrationResultDialog
                            registration={registration}
                            sportClassId={competitionClass.sport_class_id}
                            fetchClass={fetchClass}
                            round={level}
                            action={'Lägg till resultat'}
                        />
                    );
                },
            },
        );
    }

    return baseColumns;
};

export function StraightCrookedDatatable({ competitionClass, fetchClass, level, isLoading, setColumnVisibility, columnVisibility, onColumnsReady }) {
    const [sorting, setSorting] = useState(() => {
        if (competitionClass?.finished_at) {
            return [{ id: "placing", desc: false }];
        }
        return [{ id: "startNumber", desc: false }];
    });
    const [columnFilters, setColumnFilters] = useState([]);
    const { auth } = useContext(AuthContext);

    const filteredData = (competitionClass?.registrations || []).filter(registration => {
        if (registration.crossedOut) return false;

        // Round 1: everyone (not crossed out) qualifies
        if (level === 1) return true;

        const results = registration.straightCrookedResults || [];
        const maxErrors = competitionClass[`max_errors_round${level}`];

        // grab only the **previous** rounds 1…(level-1)
        const prevResults = results.filter(r => r.round < level);

        // must have a result for *every* prior round
        if (prevResults.length < level - 1) {
            return false;
        }

        // total up their faults in those prior rounds
        const totalPrevErrors = prevResults
            .reduce((sum, r) => sum + (r.errors ?? Infinity), 0);

        return totalPrevErrors <= maxErrors;
    });

    // Filter registrations based on errors for the current round, except for round 1
    // const filteredData = useMemo(() => {
    //     return (competitionClass?.registrations || []).filter((registration) => {
    //         if (registration.crossedOut) return false; // Exclude crossed-out registrations

    //         const results = registration.straightCrookedResults || [];
    //         const currentAttempt = results.find((result) => result.round === level); // Match current round
    //         const previousAttempt = results.find((result) => result.round === level - 1); // Match previous round
    //         const maxErrors = competitionClass[`max_errors_round${level}`];

    //         // **Case 1**: Omgång 1 (Level 1)
    //         if (level === 1) {
    //             return true; // Include all (except crossed-out) for Omgång 1
    //         }

    //         // **Case 2**: Has results for the current level
    //         if (currentAttempt) {
    //             const includeCurrent = currentAttempt.errors != null && currentAttempt.errors <= maxErrors;
    //             if (includeCurrent) {
    //                 return true; // Include if errors in the current round <= maxErrors
    //             }

    //             // Check if previous round qualifies the registration
    //             const includePrevious =
    //                 previousAttempt && previousAttempt.errors != null && previousAttempt.errors <= maxErrors;
    //             if (includePrevious) {
    //                 return true;
    //             }
    //             return false; // Exclude if neither round qualifies
    //         }

    //         // **Case 3**: No results for the current level, check previous level
    //         if (previousAttempt) {
    //             const include = previousAttempt.errors != null && previousAttempt.errors <= maxErrors;
    //             return include;
    //         }

    //         // Default: Exclude if no attempts exist
    //         return false;
    //     });
    // }, [competitionClass, level]);

    const tableColumns = useMemo(() => {
        return columns(fetchClass, competitionClass, level, auth, filteredData.length);
    }, [fetchClass, competitionClass, level, auth, filteredData.length]);

    useEffect(() => {
        if (onColumnsReady) {
            onColumnsReady(tableColumns);
        }
    }, [onColumnsReady]);

    const table = useReactTable({
        data: filteredData,
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            pagination: {
                pageIndex: 0,
                pageSize: 9999,
            },
        },
        meta: {
            getRowStyles: (row) => {
                // Check user permissions first
                const isPrivilegedUser = auth?.user?.superadmin ||
                    auth?.user?.bunnyadmin ||
                    auth?.user?.judge;

                // Check if class is finished
                const isClassFinished = competitionClass?.finished_at;

                // If user doesn't have permission or class is finished, don't apply styles
                if (!isPrivilegedUser || isClassFinished) {
                    return "";
                }

                // Otherwise, check if the row has a result
                const registration = row.original;
                const results = registration.straightCrookedResults || [];
                const attempt = results[level - 1] || {};
                const hasResult = attempt.errors != null || attempt.time != null;

                return hasResult ? "!bg-skhrf-blue/10" : "";
            }
        }
    });

    if (filteredData.length === 0) {
        return (
            <p>
                Inga deltagare uppfyller kriterierna för omgång {level}.
            </p>
        );
    }

    return (
        <DataTable
            table={table}
            isLoading={isLoading}
            columns={tableColumns}
        />
    );
}
